import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MapContainer, TileLayer, Marker, Tooltip as LeafletTooltip, useMap } from 'react-leaflet';
import L, { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { StockFacilityResult } from '../../../types/Stock';

const MapWrapper = styled(Box)({
  height: '100%',
  // Fills whatever height the results panel gives it (which has its own
  // min-height), so the map grows with the viewport on larger screens.
  minHeight: 0,
  '& .leaflet-container': { height: '100%', width: '100%', borderRadius: 0 },
});

interface StockFacilityMapProps {
  facilities: StockFacilityResult[];
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
}

const makeFacilityIcon = (totalCount: number, isSelected: boolean) =>
  L.divIcon({
    className: '',
    html: `<div style="
      min-width:30px;height:30px;padding:0 8px;border-radius:14px;
      background:${isSelected ? '#15803d' : '#22c55e'};
      border:2px solid ${isSelected ? '#14532d' : '#ffffff'};
      display:flex;align-items:center;justify-content:center;
      font-size:11px;font-weight:700;color:#fff;
      box-shadow:0 1px 4px rgba(0,0,0,${isSelected ? '0.45' : '0.25'});
      transform:${isSelected ? 'scale(1.25)' : 'scale(1)'};
      transition:transform 0.15s;
      white-space:nowrap;
    ">${totalCount}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });

// Pans/zooms to the selected facility without resetting the view for every render.
const PanToSelected: React.FC<{ position: LatLng | null }> = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, Math.max(map.getZoom(), 15), { animate: true });
  }, [position, map]);
  return null;
};

const FitBounds: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [coords, map]);
  return null;
};

const ResetView: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  const handleReset = () => {
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16, animate: true });
    }
  };
  return (
    <div className='leaflet-top leaflet-left' style={{ marginTop: 80, pointerEvents: 'auto' }}>
      <div className='leaflet-control leaflet-bar'>
        <a
          href='#'
          title='Reset to full view'
          role='button'
          aria-label='Reset to full view'
          onClick={(e) => {
            e.preventDefault();
            handleReset();
          }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, color: '#444', textDecoration: 'none' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8' />
            <path d='M3 3v5h5' />
          </svg>
        </a>
      </div>
    </div>
  );
};

// Facilities can share the exact same coordinates (e.g. two areas of one warehouse),
// which stacks their markers. Co-located markers are fanned out around their shared
// point so each count stays readable and individually clickable.
const spreadCoLocated = (facilities: StockFacilityResult[]): [number, number][] => {
  const groups: Record<string, number[]> = {};
  facilities.forEach((facility, index) => {
    const key = `${facility.location.latitude.toFixed(6)},${facility.location.longitude.toFixed(6)}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(index);
  });

  const positions: [number, number][] = new Array(facilities.length);
  Object.values(groups).forEach((indices) => {
    const count = indices.length;
    indices.forEach((facilityIndex, position) => {
      const { latitude, longitude } = facilities[facilityIndex].location;
      if (count === 1) {
        positions[facilityIndex] = [latitude, longitude];
        return;
      }
      const angle = (position / count) * 2 * Math.PI - Math.PI / 2;
      const radius = 0.0004;
      positions[facilityIndex] = [latitude + radius * Math.cos(angle), longitude + (radius * Math.sin(angle)) / Math.cos((latitude * Math.PI) / 180)];
    });
  });

  return positions;
};

export default function StockFacilityMap({ facilities, selectedIndex, onSelect }: StockFacilityMapProps): JSX.Element {
  const coords = useMemo(() => spreadCoLocated(facilities), [facilities]);
  const selectedPosition = selectedIndex !== null && coords[selectedIndex] ? new LatLng(coords[selectedIndex][0], coords[selectedIndex][1]) : null;
  const defaultCenter = coords.length > 0 ? new LatLng(coords[0][0], coords[0][1]) : new LatLng(0, 0);

  return (
    <MapWrapper>
      {facilities.length === 0 ? (
        // With no tiles rendered the map section would otherwise be an unbounded
        // white area, so the empty state carries the outline itself — matching
        // the table card's border alongside it.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            boxSizing: 'border-box',
            border: '1.5px solid #e5e7eb',
            borderRadius: 'inherit',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No mappable locations for this selection.
          </Typography>
        </Box>
      ) : (
        <MapContainer center={defaultCenter} zoom={13} scrollWheelZoom zoomControl attributionControl style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
          />
          <FitBounds coords={coords} />
          <PanToSelected position={selectedPosition} />
          <ResetView coords={coords} />
          {facilities.map((facility, index) => (
            <Marker
              key={index}
              position={coords[index]}
              icon={makeFacilityIcon(facility.totalCount, index === selectedIndex)}
              zIndexOffset={index === selectedIndex ? 1000 : 0}
              eventHandlers={{
                click: () => onSelect(index === selectedIndex ? null : index),
              }}
            >
              <LeafletTooltip direction='top' offset={[0, -14]}>
                <strong>{facility.facilityName ?? 'Unassigned'}</strong>
                <br />
                Total: {facility.totalCount}
                {facility.byUldType.map((uld) => (
                  <div key={uld.uldGroupId}>
                    {uld.uldGroupIataCode}: {uld.totalCount}
                  </div>
                ))}
              </LeafletTooltip>
            </Marker>
          ))}
        </MapContainer>
      )}
    </MapWrapper>
  );
}
